import { useEffect, useState } from "react";
import { Smartphone } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.enturn.recruiter";

const DISMISS_KEY = "open-in-app-dismissed";

/** Returns true only on an Android mobile browser (not iOS, not desktop). */
function isAndroidMobile(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /android/i.test(ua) && /mobile/i.test(ua);
}

/**
 * On Android mobile web, prompt the user to continue in the web app or open
 * the Android app on the Play Store. iOS mobile and desktop are unaffected.
 */
function OpenInAppDialog() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isAndroidMobile()) return;
    if (sessionStorage.getItem(DISMISS_KEY) === "true") return;
    setOpen(true);
  }, []);

  const handleContinueInWeb = () => {
    sessionStorage.setItem(DISMISS_KEY, "true");
    setOpen(false);
  };

  const handleOpenApp = () => {
    sessionStorage.setItem(DISMISS_KEY, "true");
    setOpen(false);
    window.open(PLAY_STORE_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        showCloseButton={false}
        className="max-w-xs gap-0 overflow-hidden rounded-3xl p-0 text-center shadow-xl"
      >
        {/* header band */}
        <div className="from-primary/10 to-primary/5 flex flex-col items-center bg-gradient-to-b px-6 pb-6 pt-7">
          <div className="bg-primary text-primary-foreground ring-primary/20 flex size-16 items-center justify-center rounded-2xl shadow-lg ring-8">
            <Smartphone className="size-8" />
          </div>
          <DialogTitle className="mt-4 text-center text-base font-semibold">
            Open in the Enturn Recruiter app?
          </DialogTitle>
          <DialogDescription className="mt-1.5 text-center text-sm leading-relaxed">
            For a better experience on your phone, continue in the app.
          </DialogDescription>
        </div>

        {/* actions */}
        <div className="flex flex-col gap-2 px-6 pb-6">
          <Button
            variant="outline"
            onClick={handleContinueInWeb}
            className="h-11 w-full rounded-xl text-sm font-medium"
          >
            Continue in web
          </Button>
          <Button
            size="lg"
            onClick={handleOpenApp}
            className="h-11 w-full rounded-xl text-sm font-semibold"
          >
            Continue in app
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default OpenInAppDialog;
